import i18nInstance from 'i18n/i18nInstance'
import { STATIC_IMAGE_BASE } from '@mc/constants'
import { Button, IconArrowRightDefault } from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { paths } from '@mc/paths'
import { TrackingContainer } from '@mc/visibility'
import Image from 'components/LegacyImage'
import {
  trackContactSalesClicked,
  trackEnterpriseLearnMoreClicked,
  trackMAWCTAViewed,
} from './utils/events'
import classes from './BusinessBanner.module.scss'

interface BusinessBannerProps {
  isLightTheme?: boolean
  pageSource: string
  className?: string
}

const BusinessBanner = ({
  isLightTheme = false,
  pageSource,
  className = '',
}: BusinessBannerProps) => {
  const { t } = useTranslation('@mc/next')

  return (
    <TrackingContainer onFirstVisible={() => trackMAWCTAViewed(pageSource)}>
      <section
        data-testid='business-section'
        className={`container mc-my-10 ${className}`}
      >
        <div className='row'>
          <div
            className={`col-12 d-flex justify-content-center align-items-center flex-column flex-md-row mc-p-0 mc-m-0 flex-md-nowrap ${classes.banner}`}
          >
            <div
              className={`${
                isLightTheme ? 'mc-bg-neutral-100' : 'mc-bg-neutral-900'
              } mc-corners--md ${classes.background}`}
            />
            <div
              className={`position-relative mc-corners--lg mc-overflow--hidden ${classes.img}`}
            >
              <Image
                src={`${STATIC_IMAGE_BASE}/maw_banner_image.png`}
                layout='fill'
                alt=''
                objectFit='cover'
                data-testid={`maw-banner-l&d-img`}
              />
            </div>
            <div
              className={`mc-mx-md-9 mc-mx-4 mc-mr-xl-0 d-flex flex-column align-items-center align-items-md-start mc-my-6 mc-my-md-0 ${classes.info}`}
            >
              <div className={`position-relative ${classes.logo}`}>
                <Image
                  src={`${STATIC_IMAGE_BASE}/${
                    isLightTheme ? 'maw-logo-light' : 'maw-logo'
                  }.png`}
                  alt='MasterClass Logo'
                  data-testid='maw-banner-logo'
                  layout='fill'
                  objectFit='cover'
                />
              </div>
              <h1
                className={`mc-text--brand mc-text--uppercase mc-text-d1 mc-mt-2 mc-mb-6 ${
                  classes.title
                } ${isLightTheme ? classes.titleLight : ''}`}
              >
                {t('businessBanner.title')}
              </h1>
              <p
                className={`mc-text--2-lines-max mc-opacity--hinted mc-text--center mc-text-md--left ${classes.description}`}
              >
                {t('businessBanner.description')}
              </p>
              <div className='mc-mt-8 d-flex flex-md-row flex-column align-items-center'>
                <Button
                  className='mc-mr-4'
                  href={paths.enterprisePurchase}
                  as='a'
                  onClick={() => trackContactSalesClicked(pageSource)}
                >
                  {t('businessBanner.primaryCta')}
                </Button>
                <Button
                  kind='link'
                  href={paths.enterpriseHomepage}
                  as='a'
                  className='mc-mt-4 mc-mt-md-0'
                  onClick={() => trackEnterpriseLearnMoreClicked(pageSource)}
                >
                  {t('businessBanner.secondaryCta')}
                  <IconArrowRightDefault size={2} className='mc-ml-3' />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </TrackingContainer>
  )
}

export default withI18nContext(i18nInstance)(BusinessBanner)
